//#region Imports
import React, { useEffect, useState, useMemo } from 'react';
import { SafeAreaView, StyleSheet, useWindowDimensions, FlatList, Platform, View, ScrollView } from 'react-native';
import { useTheme, useNavigation } from '@react-navigation/native';
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomButton from "../../../components/CustomButton/CustomButton";
import CustomDropDownWithIcons from '../../../components/CustomDropDownWithIcons';
import { Card } from "react-native-paper";
import { useTranslation } from 'react-i18next';
import Modal from "react-native-modal";
import { formatDateForUI } from '../../../constants/FormatDate';
//#endregion

const MyTimesheetsScreen = (props) => {
  //#region Const Definitions
  const { width, height } = useWindowDimensions();
  const { t } = useTranslation();
  const navigation = useNavigation();
  const { colors } = useTheme();
  const styles = makeStyles(colors, height, width);

  // For new timsheet modal
  const [modalNewTSVisible, setModalNewTSVisible] = useState(false);
  const [TSError, setTSError] = useState(false);
  const [newYear, setNewYear] = useState('');
  const [newMonth, setNewMonth] = useState('');
  const [newPeriod, setNewPeriod] = useState('');
  const [newWeek, setNewWeek] = useState('');

  // Function handling press of status
  const handlePress = (item) => {
    if (props.isMonthlyTimesheet) {
      const monthlyTimesheetData = {
        tsnoUIA: item.periodA?.tsnoUI || null,
        tsnoUIB: item.periodB?.tsnoUI || null,
      };
      props.selectMyTimesheetDetailsScreen()
      props.setTSUI(monthlyTimesheetData)
      props.setisApprover(false)
    } else {
      props.selectMyTimesheetDetailsScreen()
      props.setTSUI(item.tsnoUI)
      props.setisApprover(false)
    }
  };

  const YEARS = props.yearOptions;
  const currentYear = new Date().getFullYear();
  const allYears = [];
  for (let year = currentYear; year >= 1990; year--) {
    allYears.push({ value: year.toString(), label: year.toString() });
  }
  const MONTHS = [
    { description: 'January', value: 1 },
    { description: 'February', value: 2 },
    { description: 'March', value: 3 },
    { description: 'April', value: 4 },
    { description: 'May', value: 5 },
    { description: 'June', value: 6 },
    { description: 'July', value: 7 },
    { description: 'August', value: 8 },
    { description: 'September', value: 9 },
    { description: 'October', value: 10 },
    { description: 'November', value: 11 },
    { description: 'December', value: 12 },
  ];

  //#region State for filters
  const [filterYear, setFilterYear] = useState('All Years');
  const [filterMonth, setFilterMonth] = useState('All Months');
  const [filterPeriodA, setFilterPeriodA] = useState(true);
  const [filterPeriodB, setFilterPeriodB] = useState(true);
  const [showSubmitted, setShowSubmitted] = useState(true);
  const [showApproved, setShowApproved] = useState(true);
  const [isModalVisible, setModalVisible] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  const onFilterPressed = () => {
    setModalVisible(!isModalVisible);
  };

  const onConfirmPressed = () => {
    setModalVisible(!isModalVisible);
  }

  const setFilterAll = () => {
    setFilterPeriodA(true);
    setFilterPeriodB(true);
    setShowSubmitted(true);
    setShowApproved(true);
  }

  const onClearPressed = () => {
    setFilterYear('All Years');
    setFilterMonth('All Months');
    setFilterAll();
    setModalVisible(!isModalVisible);
  }

  // Helper function to group monthly timesheets by year-month
  const groupMonthlyTimesheets = (timesheets) => {
    if (!timesheets || timesheets.length === 0) return [];

    const grouped = {};

    timesheets.forEach(ts => {
      const key = `${ts.year}-${ts.month}`;

      if (!grouped[key]) {
        grouped[key] = {
          year: ts.year,
          month: ts.month,
          periodA: null,
          periodB: null,
          isSubmitted: false,
          isApproved: false,
        };
      }

      if (ts.period === 'A') {
        grouped[key].periodA = ts;
        grouped[key].isSubmitted = grouped[key].isSubmitted || ts.isSubmitted;
        grouped[key].isApproved = grouped[key].isApproved || ts.isApproved;
      } else if (ts.period === 'B') {
        grouped[key].periodB = ts;
        grouped[key].isSubmitted = grouped[key].isSubmitted || ts.isSubmitted;
        grouped[key].isApproved = grouped[key].isApproved || ts.isApproved;
      }
    });

    return Object.values(grouped).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  };

  const baseFilteredTimesheets = props.userTimesheetsList.filter(item => {
    if (filterYear !== 'All Years' && item.year.toString() !== filterYear) return false;
    if (filterMonth !== 'All Months' && item.month !== filterMonth) return false;

    switch (activeFilter) {
      case 'periodA':
        return item.period === 'A';
      case 'periodB':
        return item.period === 'B';
      case 'submitted':
        return item.isSubmitted;
      case 'approved':
        return item.isApproved;
      default:
        return true;
    }
  });

  const filteredTimesheets = props.isMonthlyTimesheet
    ? groupMonthlyTimesheets(baseFilteredTimesheets)
    : baseFilteredTimesheets;
  //#endregion
  //#endregion

  //#region Use Effect
  useEffect(() => {
    navigation.setOptions({
      title: t('navigate:MT')
    });
    props.loadUserTimesheets();
  }, []);
  //#endregion

  //#region Render Functions
  const TimesheetListView = ({ item }) => {
    return (
      <Card style={styles.cardstyle}>
        <Card.Content style={{ padding: 0 }}>
          <View style={styles.row}>
            <View style={styles.columLabels}>
              <CustomLabel value={item.year.toString()} type="SUBTITLE" />
            </View>
            <View style={styles.verticalLine} />
            <View style={styles.columLabels}>
              <CustomLabel value={MONTHS.find(m => m.value === item.month)?.description || ''} type="SUBTITLE" />
            </View>
            <View style={styles.verticalLine} />
            {!props.isMonthlyTimesheet && !props.isWeekly && <><View style={styles.columLabels}>
              <CustomLabel value={item.period} type="SUBTITLE" />
            </View>
              <View style={styles.verticalLine} /> </>}
            {props.isWeekly &&
              <><View style={styles.columLabels}>
                <CustomLabel value={`${formatDateForUI(item.weekStart)} - ${formatDateForUI(item.weekEnd)}`} type="SUBTITLE" />
              </View>
                <View style={styles.verticalLine} /> </>}
            {(Platform.OS === 'web') && (
              <>
                <View style={styles.columLabels}>
                  {item.isSubmitted ? (
                    <CustomButton
                      iconName="checkmark-circle-outline"
                      show="icon_HIDE"
                      type="SUBTITLE"
                      sz={Platform.OS === 'web' ? 25 : 15}
                      disabled
                    />
                  ) : (
                    <CustomButton
                      iconName="ellipse-outline"
                      show="icon_HIDE"
                      type="SUBTITLE"
                      sz={Platform.OS === 'web' ? 25 : 15}
                      disabled
                    />
                  )}
                </View>
                <View style={styles.verticalLine} />
                <View style={styles.columLabels}>
                  {item.isApproved ? (
                    <CustomButton
                      iconName="checkmark-circle-outline"
                      show="icon_HIDE"
                      type="SUBTITLE"
                      sz={Platform.OS === 'web' ? 25 : 15}
                      disabled
                    />
                  ) : (
                    <CustomButton
                      iconName="ellipse-outline"
                      show="icon_HIDE"
                      type="SUBTITLE"
                      sz={Platform.OS === 'web' ? 25 : 15}
                      disabled
                    />
                  )}
                </View>
                <View style={styles.verticalLine} />
              </>
            )}
            {/* Status Column */}
            <View style={styles.button}>
              {(!item.isSubmitted && !item.isApproved) ? (
                <CustomButton
                  type="PENDING"
                  text={t("common:assessmentpending")}
                  onPress={() => handlePress(item)}
                  show="HIDE"
                />
              ) : (item.isSubmitted && !item.isApproved) ? (
                <CustomButton
                  type="WAITING"
                  text={t("common:waitingApproval")}
                  onPress={() => handlePress(item)}
                  show="HIDE"
                />
              ) : (item.isSubmitted && item.isApproved) ? (
                <CustomButton
                  type="COMPLETE"
                  text={t("common:assessmentcomplete")}
                  onPress={() => handlePress(item)}
                  show="HIDE"
                />
              ) : (
                <CustomButton
                  type="CLOSED"
                  text={t("common:unknown")}
                  disabled={true}
                  show="HIDE"
                />
              )}
            </View>
          </View>
        </Card.Content>
      </Card>
    );
  };

  const renderTimesheet = ({ item }) => (
    <TimesheetListView item={item} />
  );

  const ListHeader = () => (
    <View style={[styles.row, styles.listHeader]}>
      <View style={styles.columLabels}>
        <CustomLabel value={t("common:year")} type="SUBTITLE" />
      </View>
      <View style={styles.verticalLine} />
      <View style={styles.columLabels}>
        <CustomLabel value={t("common:month")} type="SUBTITLE" />
      </View>
      <View style={styles.verticalLine} />
      {!props.isMonthlyTimesheet && !props.isWeekly && <>
        <View style={styles.columLabels}>
          <CustomLabel value={t("common:period")} type="SUBTITLE" />
        </View>
        <View style={styles.verticalLine} /> </>}
      {props.isWeekly && <>
        <View style={styles.columLabels}>
          <CustomLabel value={t("common:week")} type="SUBTITLE" />
        </View>
        <View style={styles.verticalLine} /> </>}
      <View style={styles.columLabels}>
        <CustomLabel value={t("common:submitted")} type="SUBTITLE" />
      </View>
      <View style={styles.verticalLine} />
      <View style={styles.columLabels}>
        <CustomLabel value={t("common:approved")} type="SUBTITLE" />
      </View>
      <View style={styles.verticalLine} />
      <View style={styles.columLabels}>
        <CustomLabel value={t("common:status")} type="SUBTITLE" />
      </View>
    </View>
  );

  //********************************************************************* 
  //* Modal to create a new custom timesheet
  //* Modal adjusted based on Monthly, weekly or periodical completion of TS
  //********************************************************************* 
  const renderNewTimesheetModal = () => {
    // Prepare options for DropDown
    const yearOptions = [...allYears];
    const monthOptions = [...MONTHS.map(m => ({ label: m.description, value: m.value }))];

    // Generate weeks for selected year and month
    const generateWeeksForMonth = (year, month) => {
      if (!year || !month) return [];
      // Convert to numbers if they're strings (to avoid false calculations)
      const yearNum = typeof year === 'string' ? parseInt(year) : year;
      const monthNum = typeof month === 'string' ? parseInt(month) : month;

      const weeks = [];

      // Create dates at noon (12:00) to avoid timezone offset issues
      const firstDay = new Date(yearNum, monthNum - 1, 1, 12, 0, 0);
      const lastDay = new Date(yearNum, monthNum, 0, 12, 0, 0);

      let currentDate = new Date(yearNum, monthNum - 1, 1, 12, 0, 0);
      const dayOfWeek = currentDate.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      currentDate.setDate(currentDate.getDate() - daysToMonday);

      // Generate weeks until we pass the last day of the month
      while (currentDate <= lastDay) {
        const weekStart = new Date(currentDate);
        const weekEnd = new Date(currentDate);
        weekEnd.setDate(weekEnd.getDate() + 6); // Add 6 days to get Sunday

        const label = `${formatDateForUI(weekStart.toISOString())}-${formatDateForUI(weekEnd.toISOString())}`;
        weeks.push({
          label: label,
          value: {
            weekStart: weekStart,
            weekEnd: weekEnd
          }
        });

        // Move to next Monday
        currentDate.setDate(currentDate.getDate() + 7);
      }

      return weeks;
    };

    const weekOptions = useMemo(() => {
      return generateWeeksForMonth(newYear, newMonth);
    }, [newYear, newMonth]);

    return (
      <View style={{ padding: 20 }}>
        <View style={{ paddingVertical: 25, alignItems: 'center' }} >
          <CustomLabel value={t('common:newTimesheet')} type="TITLE" />
        </View>
        {/* Year Dropdown */}
        <View style={[styles.inputContainer, { zIndex: 20, flexDirection: 'row', alignSelf: 'center' }]}>
          <View style={{ alignItems: 'flex-start', marginHorizontal: 25 }}>
            <CustomLabel value={t('common:year') + ': '} type="SUBTITLE" />
          </View>
          <CustomDropDownWithIcons
            defValue={newYear}
            options={yearOptions}
            value={newYear}
            setValue={setNewYear}
            type={TSError && newYear === '' ? "ERROR" : "MYPROFILE"}
          />
        </View>
        {/* Month Dropdown */}
        <View style={[styles.inputContainer, { zIndex: 10, flexDirection: 'row', alignSelf: 'center' }]}>
          <View style={{ alignItems: 'flex-start', marginHorizontal: 20 }}>
            <CustomLabel value={t('common:month') + ': '} type="SUBTITLE" />
          </View>
          <CustomDropDownWithIcons
            defValue={newMonth}
            options={monthOptions}
            value={newMonth}
            setValue={setNewMonth}
            type={TSError && newMonth === '' ? "ERROR" : "MYPROFILE"}
          />
        </View>
        {/* Period Dropdown */}
        {!props.isMonthlyTimesheet && !props.isWeekly && <View style={[styles.inputContainer, { zIndex: 5, flexDirection: 'row', alignSelf: 'center' }]}>
          <View style={{ alignItems: 'flex-start', marginHorizontal: 20 }}>
            <CustomLabel value={t('common:period') + ': '} type="SUBTITLE" />
          </View>
          <CustomDropDownWithIcons
            defValue={newPeriod}
            options={[{ value: 'A', label: 'A' }, { value: 'B', label: 'B' }]}
            value={newPeriod}
            setValue={setNewPeriod}
            type={TSError && newPeriod === '' ? "ERROR" : "MYPROFILE"}
          />
        </View>}
        {/* Week Dropdown - Only for weekly timesheets */}
        {props.isWeekly && (
          <View style={[styles.inputContainer, { zIndex: 5, flexDirection: 'row', alignSelf: 'center' }]}>
            <View style={{ alignItems: 'flex-start', marginHorizontal: 20 }}>
              <CustomLabel value={t('common:week') + ': '} type="SUBTITLE" />
            </View>
            <CustomDropDownWithIcons
              defValue={newWeek}
              options={weekOptions}
              value={newWeek}
              setValue={setNewWeek}
              type={TSError && newWeek === '' ? "ERROR" : "MYPROFILE"}
              disabled={!newYear || !newMonth}
            />
          </View>
        )}
        {/* New/Cancel Buttons */}
        <View style={{ flexDirection: 'row', justifyContent: 'center', paddingHorizontal: 50 }}>
          <CustomButton
            text={t('common:new')}
            onPress={() => {
              if (newYear === '' || newMonth === '' || (!props.isMonthlyTimesheet && !props.isWeekly && newPeriod === '') || (props.isWeekly && !newWeek)) {
                setTSError(true);
                return;
              }
              props.loadNewTimesheet(newYear, newMonth, newPeriod, newWeek)
            }}
            type="BUTTON_SAVE"
            textTop='0'
            isGradient={true}
            sz={15}
          />
          <CustomButton
            text={t('common:cancel')}
            onPress={() => {
              setTSError(false)
              setModalNewTSVisible(false)
            }}
            type="BUTTON_CANCEL"
            textTop="0"
            sz={15}
          />
        </View>
      </View>
    );
  };

  //#endregion

  //#region Filter Panel Component
  //********************************************************************* 
  //* Modal including all filtering options
  //* Modal adjusted based on Monthly or periodical completion of TS
  //********************************************************************* 
  const FilterPanel = () => {
    // Prepare options for DropDown
    const yearOptions = [{ value: 'All Years', label: 'All Years' }, ...YEARS];
    const monthOptions = [{ label: 'All Months', value: 'All Months' }, ...MONTHS.map(m => ({ label: m.description, value: m.value }))];

    return (
      <SafeAreaView>
        {/* Year Dropdown */}
        <View style={[styles.inputContainer, { zIndex: 20 }]}>
          <CustomDropDownWithIcons
            defValue={filterYear}
            options={yearOptions}
            value={filterYear}
            setValue={setFilterYear}
            type='MYPROFILE'
          />
        </View>

        {/* Month Dropdown */}
        <View style={[styles.inputContainer, { zIndex: 10 }]}>
          <CustomDropDownWithIcons
            defValue={filterMonth}
            options={monthOptions}
            value={filterMonth}
            setValue={setFilterMonth}
            type='MYPROFILE'
          />
        </View>

        <CustomButton
          text="Show All"
          type={activeFilter === 'all' ? "BORDERLESS_MODAL_SELECTED" : "BORDERLESS_MODAL"}
          onPress={() => { setActiveFilter('all'); setFilterAll() }}
        />

        {/* Period Toggle Buttons */}
        {!props.isMonthlyTimesheet && <CustomButton
          text={t('common:showperiodA')}
          type={activeFilter === 'periodA' ? "BORDERLESS_MODAL_SELECTED" : "BORDERLESS_MODAL"}
          onPress={() => { setActiveFilter('periodA'); }}
        />}

        {!props.isMonthlyTimesheet && <CustomButton
          text={t('common:showperiodB')}
          type={activeFilter === 'periodB' ? "BORDERLESS_MODAL_SELECTED" : "BORDERLESS_MODAL"}
          onPress={() => { setActiveFilter('periodB'); }}
        />}

        {/* Submitted Toggle Button */}
        <CustomButton
          text={t('common:showSubmitted')}
          type={activeFilter === 'submitted' ? "BORDERLESS_MODAL_SELECTED" : "BORDERLESS_MODAL"}
          onPress={() => { setActiveFilter('submitted'); }}
        />

        {/* Approved Toggle Button */}
        <CustomButton
          text={t('common:showApproved')}
          type={activeFilter === 'approved' ? "BORDERLESS_MODAL_SELECTED" : "BORDERLESS_MODAL"}
          onPress={() => { setActiveFilter('approved'); }}
        />
      </SafeAreaView>
    );
  };
  //#endregion
  return (
    <SafeAreaView style={styles.mainView}>
      {/* Row container for Filter and Next Timesheet buttons */}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 10, paddingTop: 10 }}>
        {/* Filter Button aligned left */}
        <CustomButton
          iconName='funnel-outline'
          show='BORDERLESS_FIXEDWIDTH'
          textTop='2'
          sz={25}
          type="BORDERLESS_FIXEDWIDTH"
          onPress={onFilterPressed}
        />
        <View style={{ flexDirection: 'column' }}>
          {/* Next Timesheet Button aligned right */}
          {<CustomButton
            onPress={() => { props.loadNextTimesheet() }}
            text={' ' + t('common:nextTimesheet')}
            iconName='enter-outline'
            show='PART'
            type='PART'
            textTop='0'
            sz={30}
          />}
          {/* New Timesheet Button aligned right */}
          {process.env.REACT_APP_CLIENT !== 'atlas' && <CustomButton
            onPress={() => { setModalNewTSVisible(true) }}
            text={t('common:newTimesheet')}
            iconName='add-outline'
            show='PART'
            type='PART'
            textTop='0'
            sz={30}
          />
          }
        </View>
      </View>
      <View style={styles.rowContainer}>
        <Modal
          isVisible={modalNewTSVisible}
          hasBackdrop={true}
          backdropColor={colors.background_approved}
          backdropOpacity={0.9}
          width={width}
          height={height}
          onBackdropPress={() => setModalNewTSVisible(false)}
          onRequestClose={() => setModalNewTSVisible(false)}
        >
          <View style={styles.centeredView}>
            <View style={[styles.modalView,]}>
              {renderNewTimesheetModal()}
            </View>
          </View>
        </Modal>

        <Modal
          isVisible={isModalVisible}
          hasBackdrop={true}
          backdropColor={colors.background_approved}
          backdropOpacity={0.9}
          onBackdropPress={() => setModalVisible(false)}
          onRequestClose={() => setModalVisible(false)}
        >
          <View style={{
            width: (Platform.OS === 'web' && width > 1200) ? width / 6 : '95%',
            alignSelf: 'center',
            justifyContent: 'space-between'
          }}>
            <FilterPanel />
            {/* Buttons to Cancel or Apply Filters */}
            <View style={[styles.approverContainer, { zIndex: -1 }]}>
              <CustomButton
                type="BORDERLESS"
                text={t('navigate:clear')}
                onPress={() => onClearPressed()}
              />
              <CustomButton
                type="BORDERLESS"
                text={t('navigate:filter')}
                onPress={() => onConfirmPressed()}
              />
            </View>
          </View>
        </Modal>
        {/* Timsheet List Container */}
        <ScrollView style={styles.listWrapper}>
          <FlatList
            data={filteredTimesheets}
            keyExtractor={(item) =>
              props.isMonthlyTimesheet
                ? `${item.year}-${item.month}`
                : item.tsnoUI
            }
            renderItem={renderTimesheet}
            ListHeaderComponent={ListHeader}
            stickyHeaderIndices={[0]}
            contentContainerStyle={{ paddingBottom: 20 }}
          />
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const makeStyles = (colors, height, width) => StyleSheet.create({
  mainView: {
    flex: 1,
    height: Platform.OS === "web" ? (height * 60) / 100 : "95%",
    justifyContent: "center",
    backgroundColor: colors.background_card,
  },
  rowContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  listWrapper: {
    felx: 1,
    width: '75%',
    padding: Platform.OS === 'web' ? 10 : 1,
    marginBottom: 10,
    height: '100%'
  },
  inputContainer: {
    marginTop: 10,
    marginBottom: 20,
  },
  /* Flat List */
  cardstyle: {
    marginVertical: Platform.OS === 'web' ? "1%" : 8,
    borderRadius: 5,
    elevation: 5,
    shadowColor: colors.card_line,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.3,
    shadowRadius: 5,
    backgroundColor: colors.background_card,
  },
  verticalLine: {
    backgroundColor: colors.buttons_primary,
    height: '100%',
    width: 1,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: 50,
  },
  listHeader: {
    backgroundColor: colors.background_card,
    borderBottomWidth: 1,
    borderBottomColor: colors.buttons_primary,
  },
  columLabels: {
    flex: 1,
    marginRight: Platform.OS === 'web' ? 0 : 3,
  },
  button: {
    flex: 1,
    marginHorizontal: Platform.OS === 'web' ? 0 : 1,
    justifyContent: "center",
    alignSelf: "center",
    alignItems: "center",
  },
  centeredView: {
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 1,
  },
  modalView: {
    margin: 20,
    backgroundColor: 'white',
    borderRadius: 20,
    padding: 35,
  },
  fullScreenModal: {
    width: '100%',
    height: '100%',
    borderRadius: 20,
    padding: 35,
    margin: 20,
  },
});

export default MyTimesheetsScreen;